import {HttpErrorResponse} from "@angular/common/http";
import {ErrorHandler, inject, Injectable, NgZone, signal} from "@angular/core";
import {ErrorResponse, WebSocketError} from "./dto/errors";

@Injectable({
    providedIn: "root",
})
export class GlobalErrorHandler implements ErrorHandler {
    private zone = inject(NgZone);

    /**
     * Signals-first API for uncaught errors.
     * This is the primary surface going forward.
     */
    readonly uncaughtError = signal<Error | null>(null);


    /**
     * Angular global error hook.
     * Normalizes all errors into {@link Error} and pushes to both signal + observable.
     */
    handleError(err: any) {
        const normalized = this.normalizeError(err);

        // Bust cache for lazy-loaded modules
        // This is not needed for PWA, but it still needs work so we keep this
        if (normalized.message && /Loading chunk [\d]+ failed/.test(normalized.message)) {
            console.error("Reloading due to missed chunk");
            window.location.reload();
            return;
        }

        // NOTE: Global errors can be raised outside Angular's zone (e.g., window errors, 3rd‑party libs).
        // We re-enter the zone to ensure signals/effects and change detection run reliably.
        this.zone.run(() => {
            this.uncaughtError.set(normalized);
        });
    }

    /**
     * Normalize any input error into a standard {@link Error}.
     */
    private normalizeError(err: any): Error {
        // Unwrap uncaught promise rejections
        if (err && err.promise && err.rejection) {
            err = err.rejection;
        }

        // There's some trickery with zone.js that makes the error object disappear. We make a copy. Idk what's happening.
        const errCpy = err;
        console.error(errCpy);

        if (errCpy instanceof HttpErrorResponse) {
            if (errCpy.status === 0 || errCpy.status === 504) {
                // Handles native browser errors and service worker (gateway timeout) errors
                return new Error("Internet disconnected");
            }
            // Extract the backend error message from the response body if available.
            const body = errCpy.error;
            if (body && typeof body === 'object' && typeof body.error === 'string') {
                return new Error(body.error);
            }
            if (body && typeof body === 'object' && typeof body.message === 'string') {
                return new Error(body.message);
            }
            return errCpy;
        }

        if (errCpy instanceof WebSocketError) {
            const info = errCpy.backendError.err_info;
            if (info?.message) {
                return new Error(info.message);
            }
            return errCpy;
        }

        if (errCpy instanceof Error) {
            return errCpy;
        }

        if (typeof errCpy === "string") {
            return new Error(errCpy);
        }

        if (this.hasMessage(errCpy)) { // To catch ErrorResponse
            return new Error(errCpy.message);
        }

        if (this.hasErrorMessage(errCpy)) { // To catch { error: "message" } payloads
            return new Error(errCpy.error);
        }

        if (Array.isArray(errCpy)) { // To catch ErrorResponse[]
            const messages = errCpy.map(e => e.message).join("\n");
            return new Error(messages);
        }

        return new Error("Unknown error: " + JSON.stringify(errCpy));
    }

    /**
     * Type guard for objects that expose an {@link error} string field.
     */
    private hasErrorMessage(value: unknown): value is { error: string } {
        return !!value && typeof value === "object" && "error" in value
            && typeof (value as { error: unknown }).error === "string";
    }

    /**
     * Type guard for objects that expose a {@link message} field.
     */
    private hasMessage(value: unknown): value is ErrorResponse {
        return !!value && typeof value === "object" && "message" in value;
    }
}
